import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag, Clock, CircleCheck as CheckCircle, Package, Truck,
  User, MapPin, Calendar, RefreshCw, TriangleAlert as AlertTriangle,
  MapPinned, Activity, CreditCard, ChevronDown, ChevronUp, Utensils, ShieldCheck,
  Ban, X
} from 'lucide-react';
import { MY_ORDERS_URL, ORDER_STATUS_URL, ORDER_DETAILS_URL, ORDER_DELIVERY_OTP_URL, ORDER_CANCEL_URL } from '../../config/api';
import { trackError, trackFood } from '../Utils/analytics';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { toDate } from '../Utils/time';
import OrderTrackingPanel from '../OrderTracking/OrderTrackingPanel';
import './UserPlacedOrdersTab.css';
import './MyActiveOrdersTab.css';

// Shape returned by GET /api/v1/myorders (plain array)
interface ActiveOrder {
  _id: string;
  order_id: number;
  user_id: number;
  kitchen_id: number;
  total_quantity: number;
  subtotal: number;
  delivery_fee: number;
  tax_amount: number;
  total_price: number;
  payment_method: string;
  payment_status: string;
  order_status: string;
  delivery_type: string;
  delivery_address: string;
  delivery_partner_id: number | null;
  created_at: string;
}

// Shape returned by GET /api/v1/orderstatus/{id}
interface OrderStatusResponse {
  order_id: number;
  order_status: string;
  payment_status: string;
  total_price: number;
}

// Shape returned by GET /api/v1/orders/{id}
interface OrderItem {
  order_item_id: number;
  food_id: number;
  food_name: string;
  quantity: number;
  price: number;
  subtotal: number;
}

interface OrderDetail {
  success: boolean;
  order_id: number;
  kitchen_name: string;
  items: OrderItem[];
  grand_total: number;
  rider: { name?: string; phone?: string } | null;
}

interface MyActiveOrdersTabProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

// Statuses where cancellation is allowed
const CANCELLABLE_STATUSES = new Set([
  'pending', 'confirmed', 'accepted', 'waiting_kitchen_confirmation',
]);

interface CancelResponse {
  success: boolean;
  message: string;
  order_id: number;
  cancelled_status: string;
  previous_status: string;
  cancellation_reason: string;
  refund: {
    status: string;
    amount: number;
    message: string;
    currency: string;
  };
}

// Normalise the many "ready" variant strings the API returns
const normalizeStatus = (status: string): string => {
  const s = (status || '').toLowerCase().trim();
  if (s === 'ready for pickup' || s === 'ready for pick up' || s === 'ready_for_pickup') return 'ready';
  if (s === 'out for delivery' || s === 'outfordelivery') return 'out_for_delivery';
  return s;
};

const ACTIVE_STATUSES = new Set([
  'pending', 'confirmed', 'accepted', 'preparing', 'picked', 'on_the_way', 'ready',
  'ready for pickup', 'ready for pick up', 'ready_for_pickup',
  'waiting_kitchen_confirmation',
  'out_for_delivery', 'out for delivery', 'outfordelivery',
]);

const STATUS_CONFIG: Record<string, { label: string; color: string; Icon: React.FC<{ size?: number }> }> = {
  waiting_kitchen_confirmation: { label: 'Awaiting Kitchen Confirmation', color: '#f59e0b', Icon: Clock },
  pending:    { label: 'Pending',          color: '#6c757d', Icon: Clock },
  confirmed:  { label: 'Confirmed',        color: '#17a2b8', Icon: CheckCircle },
  accepted:   { label: 'Accepted',         color: '#20c997', Icon: CheckCircle },
  preparing:  { label: 'Preparing',        color: '#ffc107', Icon: Package },
  ready:      { label: 'Ready',           color: '#fd7e14', Icon: Package },
  picked:     { label: 'Picked Up',        color: '#6f42c1', Icon: Truck },
  on_the_way: { label: 'On the Way',       color: '#007bff', Icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: '#0d6efd', Icon: Truck },
};

const MyActiveOrdersTab: React.FC<MyActiveOrdersTabProps> = ({ showNotification }) => {
  const [orders, setOrders] = useState<ActiveOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<number | null>(null);
  const [refreshingStatusId, setRefreshingStatusId] = useState<number | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<Record<number, OrderDetail>>({});
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [orderOtps, setOrderOtps] = useState<Record<number, string>>({});
  const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingInProgress, setCancellingInProgress] = useState(false);

  const fetchOrderOtp = async (orderId: number) => {
    try {
      const response = await authenticatedFetch(ORDER_DELIVERY_OTP_URL(orderId), {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const otp = data.delivery_otp || data.otp;
        if (otp) {
          setOrderOtps(prev => ({ ...prev, [orderId]: String(otp) }));
        }
      }
    } catch {
      // OTP fetch is best-effort; don't surface an error to the customer
    }
  };

  const fetchActiveOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(MY_ORDERS_URL, {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (response.status === 404) {
          setOrders([]);
        } else {
          setError('Failed to fetch active orders. Please try again later.');
        }
        setIsLoading(false);
        return;
      }

      const rawData = await response.json();
      const allOrders: ActiveOrder[] = Array.isArray(rawData) ? rawData : (rawData.orders ?? []);
      const activeOrders = allOrders
        .filter(o => ACTIVE_STATUSES.has((o.order_status || '').toLowerCase().trim()))
        .sort((a, b) => toDate(b.created_at).getTime() - toDate(a.created_at).getTime());
      setOrders(activeOrders);

      activeOrders.forEach(o => { fetchOrderOtp(o.order_id); });

    } catch {
      setError('Network error. Please check your connection and try again.');
      trackError('active_orders_fetch_failed', 'my_active_orders_tab');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // GET /api/v1/orderstatus/{order_id}
  const refreshOrderStatus = async (orderId: number) => {
    setRefreshingStatusId(orderId);
    try {
      const response = await authenticatedFetch(ORDER_STATUS_URL(orderId), {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (response.ok) {
        const data: OrderStatusResponse = await response.json();
        const prevOrder = orders.find(o => o.order_id === orderId);
        const prevStatus = (prevOrder?.order_status || '').toLowerCase().trim();
        const newStatus = (data.order_status || '').toLowerCase().trim();

        setOrders(prev =>
          prev
            .map(o => o.order_id === orderId ? { ...o, order_status: data.order_status } : o)
            .filter(o => ACTIVE_STATUSES.has((o.order_status || '').toLowerCase().trim()))
        );

        if (prevStatus === 'waiting_kitchen_confirmation' && newStatus === 'confirmed') {
          showNotification('success', 'Delivery Accepted', `Great news! Kitchen has accepted delivery for Order #${orderId}.`);
        } else if (prevStatus === 'waiting_kitchen_confirmation' && (newStatus === 'self_pickup' || newStatus.includes('pickup'))) {
          showNotification('warning', 'Converted to Self-Pickup', `The kitchen cannot deliver Order #${orderId}. Please collect from the kitchen.`);
        } else if (newStatus === 'out_for_delivery' || newStatus === 'out for delivery') {
          showNotification('info', 'Out for Delivery', `Order #${orderId} is on its way! Share your delivery OTP with the rider when it arrives.`);
          fetchOrderOtp(orderId);
        } else {
          showNotification('success', 'Status Refreshed', `Order #${orderId} is now ${data.order_status}`);
        }
      } else if (response.status === 404) {
        showNotification('info', 'Order Not Found', `Order #${orderId} could not be found.`);
      } else {
        showNotification('error', 'Update Failed', 'Could not refresh order status. Try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Failed to connect. Please check your connection.');
    } finally {
      setRefreshingStatusId(null);
    }
  };

  // GET /api/v1/orders/{order_id}
  const fetchOrderDetail = async (orderId: number) => {
    if (orderDetails[orderId]) {
      setDetailOrderId(prev => prev === orderId ? null : orderId);
      return;
    }
    setDetailOrderId(orderId);
    setLoadingDetailId(orderId);
    try {
      const response = await authenticatedFetch(ORDER_DETAILS_URL(orderId), {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });
      if (response.ok) {
        const detail: OrderDetail = await response.json();
        setOrderDetails(prev => ({ ...prev, [orderId]: detail }));
        trackFood('order_details_viewed', `order_${orderId}`);
      } else {
        showNotification('error', 'Failed to Load', 'Could not load order details. Try again.');
        setDetailOrderId(null);
      }
    } catch {
      showNotification('error', 'Network Error', 'Failed to connect. Please check your connection.');
      setDetailOrderId(null);
    } finally {
      setLoadingDetailId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return toDate(dateString).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatDeliveryType = (type: string): string => {
    if (type === 'delivery_partner' || type === 'Delivery_partner' || type === 'kitchen_delivery') return 'Kitchen Delivery';
    if (type === 'self_pickup') return 'Self Pickup';
    return type;
  };

  useEffect(() => {
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }} />
        <p>Loading your active orders...</p>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="user-profile-empty-state">
        <AlertTriangle size={60} />
        <p>{error}</p>
        <button onClick={fetchActiveOrders} className="user-profile-edit-btn" style={{ marginTop: '1rem' }}>
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="user-placed-orders-container">
      <div className="user-placed-orders-header">
        <div className="user-orders-title-section">
          <h4>Active Orders</h4>
          <p>Orders currently in progress</p>
        </div>
        <button onClick={fetchActiveOrders} className="user-profile-edit-btn" disabled={isLoading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="user-profile-empty-state">
          <ShoppingBag size={60} />
          <p>You have no active orders right now. Browse our featured meals to place a new order!</p>
        </div>
      ) : (
        <div className="user-orders-list">
          {orders.map(order => {
            const normalized = normalizeStatus(order.order_status);
            const config = STATUS_CONFIG[normalized];
            const StatusIcon = config?.Icon ?? Clock;
            const statusColor = config?.color ?? '#6c757d';
            const statusLabel = config?.label ?? order.order_status;
            const isDetailOpen = detailOrderId === order.order_id;
            const detail = orderDetails[order.order_id];

            return (
              <div key={order._id || order.order_id} className="user-order-card active-order-card">
                <div className="active-order-pulse-bar" style={{ background: statusColor }} />

                <div className="user-order-header">
                  <div className="user-order-id-section">
                    <h5>Order #{order.order_id}</h5>
                    <div
                      className="user-order-status-badge"
                      style={{ backgroundColor: statusColor, color: 'white' }}
                    >
                      <StatusIcon size={16} />
                      <span>{statusLabel}</span>
                    </div>
                  </div>
                  <div className="user-order-date">
                    <Calendar size={14} />
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                </div>

                <div className="user-order-details-grid">
                  <div className="user-order-detail">
                    <span className="user-detail-label">Items</span>
                    <span className="user-detail-value">{order.total_quantity}</span>
                  </div>
                  <div className="user-order-detail">
                    <span className="user-detail-label">Subtotal</span>
                    <span className="user-detail-value">£{order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="user-order-detail">
                    <span className="user-detail-label">Tax</span>
                    <span className="user-detail-value">£{order.tax_amount.toFixed(2)}</span>
                  </div>
                  <div className="user-order-detail">
                    <span className="user-detail-label">Total</span>
                    <span className="user-detail-value price">£{order.total_price.toFixed(2)}</span>
                  </div>
                  <div className="user-order-detail">
                    <span className="user-detail-label">Payment</span>
                    <span className="user-detail-value">
                      <CreditCard size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {order.payment_method}
                      {' '}
                      <span style={{
                        fontSize: '0.75rem',
                        color: order.payment_status === 'paid' ? '#28a745' : '#6c757d',
                        fontWeight: 500,
                      }}>
                        ({order.payment_status})
                      </span>
                    </span>
                  </div>
                  <div className="user-order-detail">
                    <span className="user-detail-label">Delivery</span>
                    <span className="user-detail-value">{formatDeliveryType(order.delivery_type)}</span>
                  </div>
                </div>

                <div className="user-delivery-info-section">
                  {order.delivery_address ? (
                    <div className="user-delivery-info">
                      <div className="user-delivery-type">
                        <Truck size={16} />
                        <span>Delivery to Address</span>
                      </div>
                      <div className="user-delivery-address">
                        <MapPin size={14} />
                        <span>{order.delivery_address}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="user-delivery-info">
                      <div className="user-delivery-type">
                        <User size={16} />
                        <span>Self Pickup</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery OTP card */}
                {orderOtps[order.order_id] && (
                  <div className="active-order-otp-card">
                    <div className="active-order-otp-header">
                      <ShieldCheck size={18} />
                      <span className="active-order-otp-title">Delivery OTP</span>
                    </div>
                    <div className="active-order-otp-code">{orderOtps[order.order_id]}</div>
                    <div className="active-order-otp-instructions">
                      Share this 4-digit OTP with the kitchen/delivery person ONLY when you receive or pick up your order. Do not share it before receiving the order.
                    </div>
                  </div>
                )}

                {/* Kitchen confirmation waiting banner */}
                {normalized === 'waiting_kitchen_confirmation' && (
                  <div className="active-order-waiting-banner">
                    <Clock size={15} />
                    <span>The kitchen owner is deciding whether they can deliver. If they decline, your order will be converted to self-pickup.</span>
                  </div>
                )}

                {/* Order detail panel (items + kitchen) */}
                {isDetailOpen && (
                  <div className="active-order-detail-panel">
                    {loadingDetailId === order.order_id ? (
                      <div className="active-order-detail-loading">
                        <div className="user-profile-loading-spinner" style={{ width: '20px', height: '20px' }} />
                        <span>Loading order details...</span>
                      </div>
                    ) : detail ? (
                      <>
                        {detail.kitchen_name && (
                          <div className="active-order-detail-kitchen">
                            <Utensils size={14} />
                            <span>{detail.kitchen_name}</span>
                          </div>
                        )}
                        {detail.items && detail.items.length > 0 && (
                          <div className="order-items-breakdown">
                            <span className="order-items-label">Items ordered:</span>
                            <ul className="order-items-list">
                              {detail.items.map(item => (
                                <li key={item.order_item_id} className="order-item-row">
                                  <span className="oi-name">{item.food_name}</span>
                                  <span className="oi-qty">x{item.quantity}</span>
                                  <span className="oi-price">£{item.subtotal.toFixed(2)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {detail.rider && (
                          <div className="active-order-detail-rider">
                            <User size={13} />
                            <span>Rider: <strong>{detail.rider.name ?? 'Assigned'}</strong></span>
                            {detail.rider.phone && (
                              <a href={`tel:${detail.rider.phone}`} className="active-order-rider-phone">
                                {String(detail.rider.phone)}
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                )}

                <div className="user-order-actions">
                  <button
                    onClick={() => refreshOrderStatus(order.order_id)}
                    className="user-order-action-btn status-refresh"
                    disabled={refreshingStatusId === order.order_id}
                  >
                    <Activity size={16} className={refreshingStatusId === order.order_id ? 'otp-spinning' : ''} />
                    <span>Check Status</span>
                  </button>
                  <button
                    onClick={() => fetchOrderDetail(order.order_id)}
                    className={`user-order-action-btn details${isDetailOpen ? ' track--active' : ''}`}
                    disabled={loadingDetailId === order.order_id}
                  >
                    {isDetailOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    <span>View Details</span>
                  </button>
                  <button
                    onClick={() => setTrackingOrderId(prev => prev === order.order_id ? null : order.order_id)}
                    className={`user-order-action-btn track${trackingOrderId === order.order_id ? ' track--active' : ''}`}
                  >
                    <MapPinned size={16} />
                    <span>{trackingOrderId === order.order_id ? 'Hide Tracking' : 'Track Order'}</span>
                  </button>
                  {CANCELLABLE_STATUSES.has(normalized) && cancellingOrderId !== order.order_id && (
                    <button
                      onClick={() => {
                        setCancellingOrderId(order.order_id);
                        setCancelReason('');
                      }}
                      className="user-order-action-btn cancel-order-btn"
                    >
                      <Ban size={16} />
                      <span>Cancel</span>
                    </button>
                  )}
                </div>

                {trackingOrderId === order.order_id && (
                  <OrderTrackingPanel
                    orderId={order.order_id}
                    deliveryType={order.delivery_type}
                    onClose={() => setTrackingOrderId(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancellingOrderId !== null && (
        <div className="cancel-order-modal-overlay" onClick={() => {
          if (!cancellingInProgress) {
            setCancellingOrderId(null);
            setCancelReason('');
          }
        }}>
          <div className="cancel-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-order-modal-header">
              <h4>Cancel Order #{cancellingOrderId}?</h4>
              <button
                className="cancel-order-modal-close"
                onClick={() => {
                  if (!cancellingInProgress) {
                    setCancellingOrderId(null);
                    setCancelReason('');
                  }
                }}
                disabled={cancellingInProgress}
              >
                <X size={18} />
              </button>
            </div>
            <p className="cancel-order-modal-text">
              Are you sure you want to cancel this order? This action cannot be undone.
            </p>
            <textarea
              className="cancel-order-reason-input"
              placeholder="Reason for cancellation (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              disabled={cancellingInProgress}
            />
            <div className="cancel-order-modal-actions">
              <button
                className="user-order-action-btn cancel-order-keep-btn"
                onClick={() => {
                  setCancellingOrderId(null);
                  setCancelReason('');
                }}
                disabled={cancellingInProgress}
              >
                Keep Order
              </button>
              <button
                className="user-order-action-btn cancel-order-confirm-btn"
                onClick={async () => {
                  if (cancellingOrderId === null) return;
                  setCancellingInProgress(true);
                  try {
                    const response = await authenticatedFetch(
                      ORDER_CANCEL_URL(cancellingOrderId),
                      {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
                        body: JSON.stringify({ reason: cancelReason.trim() || 'No reason provided' }),
                      }
                    );
                    if (response.ok) {
                      const data: CancelResponse = await response.json();
                      const refundMsg = data.refund?.message
                        ? ` ${data.refund.message}.`
                        : '';
                      showNotification('success', 'Order Cancelled', `Order #${cancellingOrderId} has been cancelled.${refundMsg}`);
                      setOrders(prev => prev.filter(o => o.order_id !== cancellingOrderId));
                      setCancellingOrderId(null);
                      setCancelReason('');
                    } else {
                      const errData = await response.json().catch(() => null);
                      const errMsg = errData?.detail?.[0]?.msg || errData?.detail || errData?.message || 'Could not cancel the order. It may be too late to cancel.';
                      showNotification('error', 'Cancellation Failed', typeof errMsg === 'string' ? errMsg : 'Could not cancel the order.');
                    }
                  } catch {
                    showNotification('error', 'Network Error', 'Failed to connect. Please check your connection and try again.');
                  } finally {
                    setCancellingInProgress(false);
                  }
                }}
                disabled={cancellingInProgress}
              >
                {cancellingInProgress ? (
                  <>
                    <div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                    <span>Cancelling...</span>
                  </>
                ) : (
                  <>
                    <Ban size={16} />
                    <span>Confirm Cancel</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyActiveOrdersTab;
